import React from "react";
import { motion } from "framer-motion";
import {
  UserPlus,
  ShieldCheck,
  CalendarCheck2,
  AlertTriangle,
  FileEdit,
  Info,
} from "lucide-react";

const sections = [
  {
    id: 1,
    title: "التسجيل والحساب",
    icon: <UserPlus className="w-7 h-7 text-emerald-600" />,
    content: [
      "يجب أن تكون جميع المعلومات التي تقدمها دقيقة وحديثة وصحيحة.",
      "أنت مسؤول عن الحفاظ على سرية بيانات تسجيل الدخول الخاصة بك، ويجب عليك إبلاغنا فورًا بأي استخدام غير مصرح به أو اختراق أمني لحسابك.",
      "نحتفظ بالحق في تعليق أو إلغاء حساب أي مستخدم يخالف القوانين المعمول بها أو هذه الشروط والأحكام دون إشعار مسبق.",
    ],
  },
  {
    id: 2,
    title: "استخدام المنصة",
    icon: <ShieldCheck className="w-7 h-7 text-emerald-600" />,
    content: [
      "تُستخدم المنصة حصريًا لحجز المواعيد الطبية مع الأطباء المعتمدين وتوفير مصادر معلومات صحية عامة وموثوقة لا تغني عن الاستشارة الطبية.",
      "يُمنع منعًا باتًا استخدام المنصة لأي أغراض غير قانونية، مضللة، احتيالية، أو ضارة بالمنصة، مستخدميها، أو الأطباء بأي شكل من الأشكال.",
    ],
  },
  {
    id: 3,
    title: "المواعيد والدفع",
    icon: <CalendarCheck2 className="w-7 h-7 text-emerald-600" />,
    content: [
      "يجب الالتزام بالمواعيد المحجوزة أو إلغاؤها قبل 24 ساعة على الأقل من الوقت المحدد لتجنب تطبيق رسوم الإلغاء أو عدم الاسترداد.",
      "جميع عمليات الدفع آمنة وتتم معالجتها عبر بوابات دفع إلكترونية موثوقة ومتوافقة مع المعايير العالمية، وتخضع لسياسة الاسترداد الخاصة بنا المنشورة بوضوح على الموقع.",
    ],
  },
  {
    id: 4,
    title: "إخلاء المسؤولية",
    icon: <AlertTriangle className="w-7 h-7 text-emerald-600" />,
    paragraph:
      "تعمل المنصة كوسيط رقمي لربط المرضى بالأطباء المؤهلين والمرخصين. نحن لسنا مسؤولين بأي حال من الأحوال عن النتائج الطبية، التشخيص، أو خطط العلاج المقدمة من قبل الأطباء. يتحمل مقدم الخدمة (الطبيب) وحده المسؤولية الكاملة عن الخدمات الطبية المقدمة. المعلومات الصحية المتوفرة على المنصة هي لأغراض إعلامية فقط ولا تغني عن الاستشارة الطبية المتخصصة.",
  },
  {
    id: 5,
    title: "التعديلات",
    icon: <FileEdit className="w-7 h-7 text-emerald-600" />,
    paragraph:
      "نحتفظ بالحق المطلق في تعديل، تحديث، أو تغيير هذه الشروط والأحكام في أي وقت ودون إشعار مسبق. استمرارك في استخدام المنصة بعد تاريخ نشر أي تعديلات يعتبر موافقة ضمنية منك وقبولًا كاملاً للشروط المعدلة.",
  },
];

const Terms = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 90, damping: 15 },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-gray-50 py-20 px-4 sm:px-6 lg:px-10">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-6xl mx-auto bg-white/80 backdrop-blur-xl shadow-2xl rounded-3xl p-8 md:p-12 lg:p-16 border border-gray-100"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <div className="inline-flex items-center justify-center p-4 mb-6 bg-emerald-100 rounded-2xl shadow-inner">
            <Info className="w-9 h-9 text-emerald-700" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
            الشروط والأحكام
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            نُقدر ثقتك في{" "}
            <span className="font-semibold text-emerald-700">منصتنا الطبية</span>،
            ونلتزم بتقديم خدمات موثوقة وآمنة. باستخدامك لخدماتنا، فإنك توافق على
            الالتزام بالشروط والأحكام الموضحة أدناه. يرجى قراءتها بعناية لفهم
            حقوقك والتزاماتك.
          </p>
        </motion.div>

        {/* Sections */}
        <motion.div variants={containerVariants} className="space-y-10">
          {sections.map((section) => (
            <motion.div
              key={section.id}
              variants={itemVariants}
              className="relative p-8 bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl hover:shadow-xl transition-all duration-300 group"
            >
              <div className="flex items-start gap-4 mb-5">
                <div className="flex-shrink-0 p-3 bg-emerald-50 rounded-xl shadow-sm group-hover:bg-emerald-100 transition-colors duration-300">
                  {section.icon}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 group-hover:text-emerald-700 transition-colors duration-300">
                  {section.id}. {section.title}
                </h2>
              </div>
              {section.content && (
                <ul className="list-disc list-inside space-y-3 text-gray-700 leading-relaxed text-base md:text-lg pl-5">
                  {section.content.map((item, i) => (
                    <li
                      key={i}
                      className="relative pl-2 before:content-[''] before:absolute before:right-0 before:top-2 before:w-2 before:h-2 before:bg-emerald-500 before:rounded-full"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {section.paragraph && (
                <p className="text-gray-700 leading-relaxed text-base md:text-lg pl-5 border-r-2 border-emerald-200 pt-2">
                  {section.paragraph}
                </p>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Footer */}
        <motion.div
          variants={itemVariants}
          className="mt-20 text-center text-gray-500 text-sm md:text-base font-light border-t pt-6"
        >
          <p>
            &copy; {new Date().getFullYear()} حكمني – جميع الحقوق محفوظة.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Terms;



// import React from "react";
// import { motion } from "framer-motion";
// import {
//   UserPlus,
//   ShieldCheck,
//   CalendarCheck2,
//   AlertTriangle,
//   FileEdit,
//   Info, // Changed to Info for a more generic 'terms' feel
// } from "lucide-react"; // Make sure lucide-react is installed

// const sections = [
//   {
//     id: 1,
//     title: "التسجيل والحساب",
//     icon: <UserPlus className="w-7 h-7 text-emerald-500" />,
//     content: [
//       "يجب أن تكون جميع المعلومات التي تقدمها دقيقة وحديثة وصحيحة.",
//       "أنت مسؤول عن الحفاظ على سرية بيانات تسجيل الدخول الخاصة بك، ويجب عليك إبلاغنا فورًا بأي استخدام غير مصرح به أو اختراق أمني لحسابك.",
//       "نحتفظ بالحق في تعليق أو إلغاء حساب أي مستخدم يخالف القوانين المعمول بها أو هذه الشروط والأحكام دون إشعار مسبق.",
//     ],
//   },
//   {
//     id: 2,
//     title: "استخدام المنصة",
//     icon: <ShieldCheck className="w-7 h-7 text-emerald-500" />,
//     content: [
//       "تُستخدم المنصة حصريًا لحجز المواعيد الطبية مع الأطباء المعتمدين وتوفير مصادر معلومات صحية عامة وموثوقة لا تغني عن الاستشارة الطبية.",
//       "يُمنع منعًا باتًا استخدام المنصة لأي أغراض غير قانونية، مضللة، احتيالية، أو ضارة بالمنصة، مستخدميها، أو الأطباء بأي شكل من الأشكال.",
//     ],
//   },
//   {
//     id: 3,
//     title: "المواعيد والدفع",
//     icon: <CalendarCheck2 className="w-7 h-7 text-emerald-500" />,
//     content: [
//       "يجب الالتزام بالمواعيد المحجوزة أو إلغاؤها قبل 24 ساعة على الأقل من الوقت المحدد لتجنب تطبيق رسوم الإلغاء أو عدم الاسترداد.",
//       "جميع عمليات الدفع آمنة وتتم معالجتها عبر بوابات دفع إلكترونية موثوقة ومتوافقة مع المعايير العالمية، وتخضع لسياسة الاسترداد الخاصة بنا المنشورة بوضوح على الموقع.",
//     ],
//   },
//   {
//     id: 4,
//     title: "إخلاء المسؤولية",
//     icon: <AlertTriangle className="w-7 h-7 text-emerald-500" />,
//     paragraph:
//       "تعمل المنصة كوسيط رقمي لربط المرضى بالأطباء المؤهلين والمرخصين. نحن لسنا مسؤولين بأي حال من الأحوال عن النتائج الطبية، التشخيص، أو خطط العلاج المقدمة من قبل الأطباء. يتحمل مقدم الخدمة (الطبيب) وحده المسؤولية الكاملة عن الخدمات الطبية المقدمة. المعلومات الصحية المتوفرة على المنصة هي لأغراض إعلامية فقط ولا تغني عن الاستشارة الطبية المتخصصة.",
//   },
//   {
//     id: 5,
//     title: "التعديلات",
//     icon: <FileEdit className="w-7 h-7 text-emerald-500" />,
//     paragraph:
//       "نحتفظ بالحق المطلق في تعديل، تحديث، أو تغيير هذه الشروط والأحكام في أي وقت ودون إشعار مسبق. استمرارك في استخدام المنصة بعد تاريخ نشر أي تعديلات يعتبر موافقة ضمنية منك وقبولًا كاملاً للشروط المعدلة.",
//   },
// ];

// const Terms = () => {
//   const containerVariants = {
//     hidden: { opacity: 0 },
//     visible: {
//       opacity: 1,
//       transition: {
//         staggerChildren: 0.1, // Slight delay for each section
//       },
//     },
//   };

//   const itemVariants = {
//     hidden: { opacity: 0, y: 20 },
//     visible: {
//       opacity: 1,
//       y: 0,
//       transition: {
//         type: "spring",
//         stiffness: 100,
//         damping: 10,
//       },
//     },
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-gray-50 py-16 px-4 sm:px-6 lg:px-8">
//       <motion.div
//         initial="hidden"
//         animate="visible"
//         variants={containerVariants}
//         className="max-w-6xl mx-auto bg-white shadow-2xl rounded-3xl p-8 md:p-12 lg:p-16 border border-gray-100"
//       >
//         {/* Header */}
//         <motion.div
//           variants={itemVariants}
//           className="text-center mb-16 md:mb-20"
//         >
//           <div className="inline-flex items-center justify-center p-3 mb-6 bg-emerald-100 rounded-full">
//             <Info className="w-8 h-8 text-emerald-600" />
//           </div>
//           <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight tracking-tight">
//             الشروط والأحكام
//           </h1>
//           <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
//             مرحباً بك في منصتنا الطبية الموثوقة. نُقدر ثقتك بنا ونلتزم بتقديم
//             خدمات عالية الجودة. من خلال التسجيل أو استخدام خدماتنا، فإنك توافق
//             على الالتزام بالشروط والأحكام التالية التي تحكم علاقتنا. يرجى
//             قراءتها بعناية فائقة لفهم حقوقك والتزاماتك.
//           </p>
//         </motion.div>

//         {/* Sections */}
//         <motion.div variants={containerVariants} className="space-y-12">
//           {sections.map((section) => (
//             <motion.div
//               key={section.id}
//               variants={itemVariants}
//               className="relative p-8 bg-white border border-gray-200 rounded-2xl hover:shadow-lg transition-shadow duration-300 group"
//             >
//               <div className="flex items-start gap-4 mb-4">
//                 <div className="flex-shrink-0 p-3 bg-emerald-50 rounded-lg group-hover:bg-emerald-100 transition-colors duration-300">
//                   {section.icon}
//                 </div>
//                 <h2 className="text-2xl md:text-3xl font-bold text-gray-800 group-hover:text-emerald-700 transition-colors duration-300">
//                   {section.id}. {section.title}
//                 </h2>
//               </div>
//               {section.content && (
//                 <ul className="list-outside space-y-3 text-gray-700 leading-relaxed text-base md:text-lg pr-4 border-r-2 border-emerald-200">
//                   {section.content.map((item, i) => (
//                     <li key={i} className="relative before:content-['•'] before:absolute before:right-0 before:top-0 before:text-emerald-500 before:font-bold before:text-xl before:leading-none before:mr-2">
//                       {item}
//                     </li>
//                   ))}
//                 </ul>
//               )}
//               {section.paragraph && (
//                 <p className="text-gray-700 leading-relaxed text-base md:text-lg pr-4 border-r-2 border-emerald-200 pt-2">
//                   {section.paragraph}
//                 </p>
//               )}
//             </motion.div>
//           ))}
//         </motion.div>

//         {/* Footer */}
//         <motion.div
//           variants={itemVariants}
//           className="mt-20 text-center text-gray-500 text-sm md:text-base font-light"
//         >
//           <p>
//             &copy; {new Date().getFullYear()} حكمني – جميع الحقوق محفوظة. تم
//             الإنشاء بحب.
//           </p>
//         </motion.div>
//       </motion.div>
//     </div>
//   );
// };

// export default Terms;
